import { carrierDetailsStore, setCarrierDetailsStore } from '@store/carriers';
import { userStore } from '@store/user';
import { Comments } from '@typeDefinitions/comments';
import { isAdmin, isSuperAdmin, userHasRole } from '@utils/utils';
import { unwrap } from 'solid-js/store';
import { ValidationError } from 'yup';

import {
  carrierDetailsSchema,
  carrierMaxLiabilitySchema,
} from './components/validation';

export const isCarrierDetailsReadOnly = () => {
  return (
    !isAdmin() &&
    carrierDetailsStore.carrierDetails?.id !== undefined &&
    carrierDetailsStore.carrierDetails.id > 0
  );
};

export const isCarrierDetailsReadOnlyForSuperAdmin = () => {
  return (
    carrierDetailsStore.carrierDetails?.id !== undefined &&
    carrierDetailsStore.carrierDetails.id != 0 &&
    !isSuperAdmin()
  );
};

export const carrierDetailsCanEdit = () => {
  return userHasRole('Carrier Relations') || isSuperAdmin();
};

export const vendorHoldDisabled = () => {
  if (isAdmin() && !Boolean(carrierDetailsStore.carrierDetails?.vendorHold))
    return false;
  if (isSuperAdmin()) return false;
  return true;
};

export const handleCarrierDetailsValidation = () => {
  try {
    setCarrierDetailsStore('carrierDetailsErrors', null);
    carrierDetailsSchema.validateSync(carrierDetailsStore.carrierDetails, {
      abortEarly: false,
    });

    // Partial max liability setups are allowed; only validate what is filled in.
    carrierDetailsStore.carrierMaxLiability.forEach((maxLiability) => {
      carrierMaxLiabilitySchema.validateSync(maxLiability, {
        abortEarly: false,
      });
    });
    return true;
  } catch (validationError: unknown) {
    const errors: Record<string, string[]> = {};
    if (validationError instanceof ValidationError) {
      validationError.inner.forEach((error: ValidationError) => {
        if (error.path !== undefined) {
          errors[error.path] = error.errors;
        }
      });
      setCarrierDetailsStore('carrierDetailsErrors', errors);
      return false;
    }
  }
};

export const validateFieldForCarrier = async (
  validatePath: string,
  errorPath: string,
) => {
  const updateErrors = (error: unknown) => {
    const updatedErrors = {
      ...(unwrap(carrierDetailsStore.carrierDetailsErrors) || {}),
      [errorPath]: error,
    };
    if (error === undefined || error === null) delete updatedErrors[errorPath];
    setCarrierDetailsStore(() => ({
      carrierDetailsErrors: updatedErrors as Record<string, string[]>,
    }));
  };
  try {
    await carrierDetailsSchema.validateAt(
      validatePath,
      carrierDetailsStore.carrierDetails,
    );
    updateErrors(undefined);
  } catch (validationError: unknown) {
    if (validationError instanceof ValidationError) {
      updateErrors(validationError.errors);
    }
  }
};

export const canDeleteCarrierComment = (comment: Comments) => {
  if (
    carrierDetailsStore.carrierDetails &&
    carrierDetailsStore.carrierDetails.ratings.filter(
      (x) => x.commentId === comment.id,
    ).length > 0
  ) {
    return false;
  }

  if (isAdminOrOriginalAuthor(comment)) {
    return true;
  }

  return false;
};

export const isAdminOrOriginalAuthor = (comment: Comments) => {
  if (isAdmin()) return true;
  if (isSuperAdmin()) return true;
  if (isOriginalAuthor(comment)) return true;
  return false;
};

const isOriginalAuthor = (comment: Comments) => {
  return comment.userId === userStore.user.id;
};

export const preventDashFn = (e: KeyboardEvent) => {
  if (e.key === '-') {
    e.preventDefault();
  }
};
